import {
  selectActualBillableAgent,
  selectActualFeatureFlags,
  selectActualPermissions,
  selectIsStaticSalesDemo,
  selectSelfPermissions,
} from 'containers/App/selectors'

// The trial/dashboard/POV flags gate visibility of the feature; whether it's
// actually usable is decided by the permission + billable checks below.
const hasPhishingSocFeatureFlag = (ff) =>
  Boolean(
    ff?.ENABLE_THEMIS_AI_TRIAL_CAMPAIGN ||
    ff?.ENABLE_THEMIS_AI_ANALYSIS ||
    ff?.ENABLE_THEMIS_AI_ANALYSIS_DASHBOARD ||
    ff?.POV_COSTUMER_FF
  )

const hasPhishingSocAccess = (permissions) =>
  !(permissions?.phishingSocAccess === false || permissions?.phishing_soc_access === false)

// webapp/rest/utils.py only sets ff.phishSoc (from company.billable_agent.phish_soc)
// when ENABLE_THEMIS_AI_ANALYSIS is on — for POV/trial/dashboard-only tenants the key
// is simply absent, not false. So `undefined` must read as "unknown, not billed off"
// and stay allowed; only an explicit `false` denies. Same story for
// selectActualBillableAgent(state)?.phishSoc, which is undefined for MSP children
// (no billableAgent field there) and must not deny in that case either.
const isBillableAgentEnabled = (ff, billableAgent) =>
  ff?.phishSoc !== false && billableAgent?.phishSoc !== false

// Combines the FF gate, the static-demo guard, the billable `phishSoc` add-on and
// the ACTUAL (MSP-selected) company's phishing_soc_access permission, matching the
// backend checks in webapp/rest/themis_token.py `generate_themis_token`: 403 unless
// has_phishing_soc_access(report.company), and 403 unless
// report.company.billable_agent.phish_soc — checked under every FF (TRIAL /
// DASHBOARD / POV included), not just ENABLE_THEMIS_AI_ANALYSIS, but the FE-visible
// `ff.phishSoc` is only ever populated under ENABLE_THEMIS_AI_ANALYSIS, so it reads
// as "unknown" (allowed) rather than "off" everywhere else — see
// isBillableAgentEnabled. Use this for anything keyed off a report's company
// (getAgentTokenEpic, the PhishingSOCAgent card).
export const selectCanUsePhishingSocAgent = (state) => {
  const ff = selectActualFeatureFlags(state)
  const isStaticDemo = selectIsStaticSalesDemo(state)
  const permissions = selectActualPermissions(state)
  const billableAgent = selectActualBillableAgent(state)
  return (
    !isStaticDemo &&
    hasPhishingSocAccess(permissions) &&
    isBillableAgentEnabled(ff, billableAgent) &&
    hasPhishingSocFeatureFlag(ff)
  )
}

// Same FF/demo/billable gate as selectCanUsePhishingSocAgent, but scoped to the SELF
// (logged-in user's own) company's phishing_soc_access permission — matching
// `generate_agent_status_token`, which is checked against the user's own company
// (has_phishing_soc_access(request.company)). It does not look at
// billable_agent.phish_soc on the backend, but denying here when the FE-visible
// flag is explicitly off still stops a poll the backend would reject anyway.
export const selectCanUseSelfPhishingSocAgent = (state) => {
  const ff = selectActualFeatureFlags(state)
  const isStaticDemo = selectIsStaticSalesDemo(state)
  const permissions = selectSelfPermissions(state)
  const billableAgent = selectActualBillableAgent(state)
  return (
    !isStaticDemo &&
    hasPhishingSocAccess(permissions) &&
    isBillableAgentEnabled(ff, billableAgent) &&
    hasPhishingSocFeatureFlag(ff)
  )
}

export const selectAgentToken = (state) => state.agents.agentToken

export const selectAgentHostname = (state) => state.agents.agentHostname

export const selectAgentTokenExpiresAt = (state) => state.agents.agentTokenExpiresAt

export const selectAgentTokenLoading = (state) => state.agents.loading.agentToken

export const selectAgentTokenError = (state) => state.agents.error.agentToken

export const selectStatusToken = (state) => state.agents.statusToken

export const selectStatusHostname = (state) => state.agents.statusHostname

export const selectStatusTokenExpiresAt = (state) => state.agents.statusTokenExpiresAt

export const selectStatusTokenLoading = (state) => state.agents.loading.agentStatusToken

export const selectStatusTokenError = (state) => state.agents.error.agentStatusToken

export const selectAgentStatusBatch = (state) => state.agents.agentStatusBatch

export const selectAgentStatusBatchLoading = (state) => state.agents.loading.agentStatusBatch

export const selectAgentStatusBatchError = (state) => state.agents.error.agentStatusBatch

// The credit balance lives in ./creditSelectors, derived from billing/status.

export const selectPhishingSocTrial = (state) => state.agents.phishingSocTrial

export const selectShowMeBanner = (state) => state.agents.showMeBanner

export const selectGetPriceBanner = (state) => state.agents.getPriceBanner

export const selectShowAtoCampaignBanner = (state) => state.agents.showAtoCampaignBanner

export const selectIsCampaignActive = (state) => state.agents.isCampaignActive

export const selectPhishingSocTrialLoading = (state) => state.agents.loading.phishingSocTrial

export const selectPhishingSocTrialError = (state) => state.agents.error.phishingSocTrial

export const selectAgentLoading = (state) => state.agents.loading

export const selectAgentError = (state) => state.agents.error

export const selectIncidentIdToEidMap = (state) => state.agents.incidentIdToEidMap

export const selectIncidentIdToReportType = (state) => state.agents.incidentIdToReportType

export const selectThemisSummaryLoading = (state) => state.agents.loading.themisSummary

export const selectThemisSummaryError = (state) => state.agents.error.themisSummary

export const selectIncidentIdWithThemisSummary = (state) => state.agents.incidentIdWithThemisSummary
